<!-- Header -->
<ibero-header></ibero-header>

<!-- Thematics filter section -->
<section class="tags-wrapper" role="navigation" aria-label="Filtros de temáticas">
    <h2 class="sr-only">Filtros por temática</h2>
    <a class="sr-only skip-link" href="#timeline">Saltar al contenido principal</a>
    
    <!-- Thematic error notification -->
    @if (errorThematics()) {
        <div class="error-notification">
            <p class="error-message">{{ errorThematics() }}</p>
            <button (click)="clearThematicsError()" class="btn-dismiss" aria-label="Cerrar notificación">×</button>
        </div>
    }

    <!-- Loading thematics -->
    @if (isLoadingThematics()) {
        <div class="tags-loading">
            <div class="loading-spinner-small"></div>
            <span>Cargando temáticas...</span>
        </div>
    }

    <!-- Thematics scroller -->
    @if (hasThematics() && !isLoadingThematics()) {
        <div class="tags-scroller" role="group" aria-label="Filtros disponibles">
            @for (thematic of thematics(); track trackByThematicId($index, thematic)) {
                <button
                    type="button"
                    class="tag" 
                    [class.active]="selectedThematic()?.id === thematic.id"
                    [style.backgroundColor]="selectedThematic()?.id === thematic.id ? getThematicColor(thematic) : ''"
                    (click)="toggleThematicFilter(thematic)"
                    [attr.aria-pressed]="selectedThematic()?.id === thematic.id"
                    [attr.aria-label]="'Filtrar por ' + thematic.name">
                    {{ thematic.name }}
                </button>
            }
        </div>
    }

    <!-- Active filter indicator -->
    @if (isThematicSelected()) {
        <div class="active-filter">
            <span>Filtrando por: <strong>{{ selectedThematic()?.name }}</strong></span>
            <button 
                (click)="clearThematicFilter()" 
                class="btn-clear-filter"
                aria-label="Limpiar filtro">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Limpiar
            </button>
        </div>
    }
</section>

<!-- Main timeline content -->
<main class="page-wrapper" id="timeline" role="main" aria-label="Línea de tiempo de publicaciones">
    
    <!-- Posts error notification -->
    @if (errorPosts()) {
        <div class="error-notification">
            <p class="error-message">{{ errorPosts() }}</p>
            <button (click)="clearPostsError()" class="btn-dismiss" aria-label="Cerrar notificación">×</button>
        </div>
    }

    <!-- Loading posts -->
    @if (isLoadingPosts()) {
        <div class="loading-state">
            <div class="loading-spinner"></div>
            <p>Cargando publicaciones...</p>
        </div>
    }

    <!-- Posts list -->
    @if (hasPosts() && !isLoadingPosts()) {
        @for (post of filteredPosts(); track trackByPostId($index, post)) {
            <article class="post-wrapper" [attr.aria-label]="'Publicación de ' + post.user.name">
                <!-- Post header -->
                <header class="post-header">
                    <div class="avatar-wrapper">
                        <div class="avatar-image">
                            <img 
                                [src]="post.user.picture" 
                                [alt]="'Avatar de ' + post.user.name"
                                loading="lazy"
                                (error)="$any($event.target).src = 'assets/images/default-avatar.png'" />
                        </div>
                        <div class="user-info">
                            <p class="user-name">{{ post.user.name }}</p>
                            <p class="post-time">
                                <time [attr.datetime]="post.created_date">
                                    {{ post.created_date | timeAgo }}
                                </time>
                            </p>
                        </div>
                    </div>
                </header>

                <!-- Post content -->
                <div class="post-content">
                    <!-- Thematic badge -->
                    @if (post.thematic?.name) {
                        <span 
                            class="thematic-badge"
                            [style.backgroundColor]="getThematicColor(post.thematic)"
                            [attr.aria-label]="'Temática: ' + (post.thematic?.name || '')">
                            {{ post.thematic?.name }}
                        </span>
                    }

                    <!-- Media content -->
                    @if (post.post_info.post_type === 'IMAGE' || post.post_info.post_type === 'GIF') {
                        <img 
                            class="post-image"
                            [src]="post.post_info.url" 
                            [alt]="post.post_info.text || 'Imagen de la publicación'"
                            loading="lazy"
                            (error)="$any($event.target).src = 'assets/images/placeholder.png'" />
                    }
                    @else if (post.post_info.post_type === 'VIDEO') {
                        <video 
                            class="post-video"
                            [src]="post.post_info.url" 
                            controls
                            preload="metadata"
                            [attr.aria-label]="'Video: ' + (post.post_info.text || 'Sin descripción')">
                            Tu navegador no soporta el elemento de video.
                        </video>
                    }
                    @else if (post.type === 'SURVEY') {
                        <ibero-survey 
                            [post_id]="post.id" 
                            [survey]="post.post_info">
                        </ibero-survey>
                    }
                    @else {
                        <div class="text-only-post">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"></path>
                                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"></path>
                            </svg>
                            <p>Publicación de texto</p>
                        </div>
                    }
                </div>

                <!-- Post description -->
                <footer class="post-description">
                    @if (post.type !== 'SURVEY') {
                        <!-- Quick action button -->
                        @if (post?.quick_action?.name) {
                            <button
                                type="button"
                                class="link-quick-action" 
                                [style.backgroundColor]="getThematicColor(post.quick_action)"
                                (click)="openQuickAction(post.quick_action)"
                                [disabled]="!post.quick_action?.url"
                                [attr.aria-label]="'Acción rápida: ' + (post.quick_action?.name || '')">
                                {{ post.quick_action?.name }}
                                @if (post.quick_action?.url) {
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"></path>
                                        <path d="M15 3h6v6M10 14L21 3"></path>
                                    </svg>
                                }
                            </button>
                        }

                        <!-- Post text -->
                        @if (post.post_info.text) {
                            <p class="post-text" [title]="post.post_info.text">
                                {{ post.post_info.text }}
                            </p>
                        }
                    }

                    <!-- Read more link -->
                    <!-- <a class="read-more" [attr.aria-label]="'Leer más sobre: ' + post.post_info.text">
                        Ver más
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M5 12h14M12 5l7 7-7 7"></path>
                        </svg>
                    </a> -->
                </footer>
            </article>
        }

        <!-- Posts count info -->
        <div class="posts-info" role="status" aria-live="polite">
            @if (isThematicSelected()) {
                <p>Mostrando {{ postsCount().filtered }} de {{ postsCount().total }} publicaciones</p>
            } @else {
                <p>{{ postsCount().total }} publicaciones totales</p>
            }
        </div>
    }

    <!-- Empty state - no posts at all -->
    @if (isEmpty()) {
        <div class="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <path d="M21 15l-5-5L5 21"></path>
            </svg>
            <h3>No hay publicaciones disponibles</h3>
            <p>No se encontraron publicaciones en este momento.</p>
            <button (click)="retryLoadPosts()" class="btn-retry">
                Reintentar
            </button>
        </div>
    }

    <!-- Empty filtered state -->
    @if (showEmptyFiltered()) {
        <div class="empty-filtered-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
            </svg>
            <h3>No hay publicaciones con esta temática</h3>
            <p>No se encontraron publicaciones para "{{ selectedThematic()?.name }}"</p>
            <button (click)="clearThematicFilter()" class="btn-retry">
                Ver todas las publicaciones
            </button>
        </div>
    }
</main>

<!-- Footer -->
<ibero-footer></ibero-footer>